// src/pages/vendor/Settings.jsx
import React, { useState, useEffect } from "react";
import { settingsApi } from "../../api/settingsApi";
import { vendorApi } from "../../api/vendorApi";
import {
  Box,
  Paper,
  Typography,
  Switch,
  FormControlLabel,
  TextField,
  Button,
  Stack,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  useMediaQuery,
  useTheme,
  Chip,
  InputAdornment,
} from "@mui/material";
import {
  Notifications,
  Store,
  Language,
  Save,
  Refresh,
  LocalShipping,
  Receipt,
  AttachMoney,
  DeliveryDining,
} from "@mui/icons-material";

const Settings = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  const [logisticsCompanies, setLogisticsCompanies] = useState([]);

  const [settings, setSettings] = useState({
    emailNotifications: false,
    orderNotifications: false,
    lowStockAlerts: false,
    marketingEmails: false,
    autoAcceptOrders: false,
    enableReviews: false,
    showVendorInfo: false,
    currency: "",
    language: "",
    timezone: "",
    minimumStockAlert: 0,
    shippingCost: 0,
    taxRate: 0,
    freeShippingThreshold: null,
    shippingMethod: "",
    logisticsCompany: "",
    estimatedDelivery: 0,
  });

  useEffect(() => {
    fetchSettings();
    fetchLogisticsCompanies();
  }, []);

  const fetchLogisticsCompanies = async () => {
    try {
      const response = await settingsApi.getLogisticsPricing();
      let companies = [];
      if (response?.data?.data) {
        companies = response.data.data;
      } else if (response?.data) {
        companies = response.data;
      } else if (Array.isArray(response)) {
        companies = response;
      }
      // Filter only active companies
      const activeCompanies = companies.filter(c => c.isActive === true || c.isActive === 1 || c.isActive === '1');
      setLogisticsCompanies(activeCompanies);
    } catch (err) {
      console.error("Failed to fetch logistics companies:", err);
    }
  };

  const fetchSettings = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await settingsApi.getSettings();
      const generalSettings = response.settings || response.data?.settings || {};

      let shippingTaxSettings = {};
      try {
        const shippingTaxResponse = await vendorApi.getShippingTaxSettings();
        if (shippingTaxResponse.success || shippingTaxResponse.data?.success) {
          shippingTaxSettings = shippingTaxResponse.data || shippingTaxResponse.data?.data || {};
        }
      } catch (err) {
        console.error("Failed to fetch shipping tax settings:", err);
      }

      setSettings({
        emailNotifications: generalSettings.emailNotifications === true || generalSettings.emailNotifications === 1,
        orderNotifications: generalSettings.orderNotifications === true || generalSettings.orderNotifications === 1,
        lowStockAlerts: generalSettings.lowStockAlerts === true || generalSettings.lowStockAlerts === 1,
        marketingEmails: generalSettings.marketingEmails === true || generalSettings.marketingEmails === 1,
        autoAcceptOrders: generalSettings.autoAcceptOrders === true || generalSettings.autoAcceptOrders === 1,
        enableReviews: generalSettings.enableReviews === true || generalSettings.enableReviews === 1,
        showVendorInfo: generalSettings.showVendorInfo === true || generalSettings.showVendorInfo === 1,
        currency: generalSettings.currency || "",
        language: generalSettings.language || "",
        timezone: generalSettings.timezone || "",
        minimumStockAlert: generalSettings.minimumStockAlert || 0,
        shippingCost: shippingTaxSettings.shippingCost || 0,
        taxRate: shippingTaxSettings.taxRate || 0,
        freeShippingThreshold: shippingTaxSettings.freeShippingThreshold !== undefined ? shippingTaxSettings.freeShippingThreshold : null,
        shippingMethod: shippingTaxSettings.shippingMethod || "",
        logisticsCompany: shippingTaxSettings.logisticsCompany || "",
        estimatedDelivery: shippingTaxSettings.estimatedDelivery || 0,
      });
    } catch (error) {
      setError("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      // Prepare shipping tax settings
      const shippingTaxSettings = {
        shippingCost: parseFloat(settings.shippingCost) || 0,
        taxRate: parseFloat(settings.taxRate) || 0,
        freeShippingThreshold: settings.freeShippingThreshold !== null ? parseFloat(settings.freeShippingThreshold) : null,
        shippingMethod: settings.shippingMethod || "flat",
        logisticsCompany: settings.logisticsCompany || "default",
        estimatedDelivery: parseInt(settings.estimatedDelivery) || 3,
      };

      // Prepare general settings
      const generalSettings = {
        emailNotifications: settings.emailNotifications ? 1 : 0,
        orderNotifications: settings.orderNotifications ? 1 : 0,
        lowStockAlerts: settings.lowStockAlerts ? 1 : 0,
        marketingEmails: settings.marketingEmails ? 1 : 0,
        autoAcceptOrders: settings.autoAcceptOrders ? 1 : 0,
        enableReviews: settings.enableReviews ? 1 : 0,
        showVendorInfo: settings.showVendorInfo ? 1 : 0,
        currency: settings.currency || "ETB",
        language: settings.language || "am",
        timezone: settings.timezone || "Africa/Addis_Ababa",
        minimumStockAlert: parseInt(settings.minimumStockAlert) || 10,
      };

      // Save general settings
      await settingsApi.updateSettings(generalSettings);
      
      // ✅ Save shipping tax settings including logistics
      const response = await vendorApi.updateShippingTaxSettings(shippingTaxSettings);

      if (response?.success) {
        // Update local state with returned data
        if (response?.data) {
          setSettings((prev) => ({
            ...prev,
            shippingCost: response.data.shippingCost || prev.shippingCost,
            taxRate: response.data.taxRate || prev.taxRate,
            freeShippingThreshold: response.data.freeShippingThreshold !== undefined ? response.data.freeShippingThreshold : prev.freeShippingThreshold,
            shippingMethod: response.data.shippingMethod || prev.shippingMethod,
            logisticsCompany: response.data.logisticsCompany || prev.logisticsCompany,
            estimatedDelivery: response.data.estimatedDelivery || prev.estimatedDelivery,
          }));
        }
        
        setSuccess("Settings saved successfully!");
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(response?.message || "Failed to save settings");
      }
    } catch (error) {
      console.error("Error saving settings:", error);
      setError(error?.response?.data?.message || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const selectedLogistics = logisticsCompanies.find(
    (l) => l.company === settings.logisticsCompany,
  );

  return (
    <Box sx={{ pb: { xs: 12, sm: 10, md: 8 } }}>
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3, borderRadius: 3 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          spacing={2}
        >
          <Box>
            <Typography variant={isMobile ? "h6" : "h5"} fontWeight="bold">
              Settings
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Configure your store settings
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} width={{ xs: "100%", sm: "auto" }}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={fetchSettings}
              disabled={saving}
              size={isMobile ? "small" : "medium"}
              fullWidth={isMobile}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<Save />}
              onClick={handleSave}
              disabled={saving}
              size={isMobile ? "small" : "medium"}
              fullWidth={isMobile}
            >
              {saving ? "Saving..." : "Save Settings"}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2, borderRadius: 2 }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}
      {success && (
        <Alert
          severity="success"
          sx={{ mb: 2, borderRadius: 2 }}
          onClose={() => setSuccess(null)}
        >
          {success}
        </Alert>
      )}

      {/* Notification Settings */}
      <Card sx={{ mb: 3, borderRadius: 3 }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
            <Notifications color="primary" />
            <Typography variant="h6" fontWeight="bold">
              Notification Settings
            </Typography>
          </Stack>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.emailNotifications}
                    onChange={(e) =>
                      handleChange("emailNotifications", e.target.checked)
                    }
                    color="primary"
                  />
                }
                label="Email Notifications"
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", ml: 2 }}
              >
                Receive email updates about your store
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.orderNotifications}
                    onChange={(e) =>
                      handleChange("orderNotifications", e.target.checked)
                    }
                    color="primary"
                  />
                }
                label="Order Notifications"
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", ml: 2 }}
              >
                Get notified when new orders arrive
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.lowStockAlerts}
                    onChange={(e) =>
                      handleChange("lowStockAlerts", e.target.checked)
                    }
                    color="primary"
                  />
                }
                label="Low Stock Alerts"
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", ml: 2 }}
              >
                Get alerts when products are running low
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.marketingEmails}
                    onChange={(e) =>
                      handleChange("marketingEmails", e.target.checked)
                    }
                    color="primary"
                  />
                }
                label="Marketing Emails"
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", ml: 2 }}
              >
                Receive marketing tips and updates
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Store Settings */}
      <Card sx={{ mb: 3, borderRadius: 3 }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
            <Store color="primary" />
            <Typography variant="h6" fontWeight="bold">
              Store Settings
            </Typography>
          </Stack>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.autoAcceptOrders}
                    onChange={(e) =>
                      handleChange("autoAcceptOrders", e.target.checked)
                    }
                    color="primary"
                  />
                }
                label="Auto-Accept Orders"
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", ml: 2 }}
              >
                Automatically accept new orders without manual approval
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.enableReviews}
                    onChange={(e) =>
                      handleChange("enableReviews", e.target.checked)
                    }
                    color="primary"
                  />
                }
                label="Enable Product Reviews"
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", ml: 2 }}
              >
                Allow customers to review your products
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.showVendorInfo}
                    onChange={(e) =>
                      handleChange("showVendorInfo", e.target.checked)
                    }
                    color="primary"
                  />
                }
                label="Show Vendor Information"
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", ml: 2 }}
              >
                Display your store information to customers
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Shipping, Tax & Logistics */}
      <Card sx={{ mb: 3, borderRadius: 3, border: "2px solid #1976d2" }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 2 }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <LocalShipping color="primary" />
              <Typography variant="h6" fontWeight="bold">
                Shipping, Tax & Logistics
              </Typography>
              <Chip
                label="Per Vendor"
                size="small"
                color="info"
                sx={{ ml: 1 }}
              />
            </Stack>
          </Stack>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Shipping Cost"
                type="number"
                value={settings.shippingCost ?? 0}
                onChange={(e) =>
                  handleChange("shippingCost", parseFloat(e.target.value) || 0)
                }
                size={isMobile ? "small" : "medium"}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AttachMoney />
                    </InputAdornment>
                  ),
                  inputProps: { min: 0, step: 0.01 },
                }}
                helperText="Default shipping cost for orders"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Tax Rate (%)"
                type="number"
                value={settings.taxRate ?? 0}
                onChange={(e) =>
                  handleChange("taxRate", parseFloat(e.target.value) || 0)
                }
                size={isMobile ? "small" : "medium"}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Receipt />
                    </InputAdornment>
                  ),
                  inputProps: { min: 0, max: 100, step: 0.01 },
                }}
                helperText="Tax percentage applied to orders"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Free Shipping Threshold"
                type="number"
                value={settings.freeShippingThreshold ?? ""}
                onChange={(e) =>
                  handleChange(
                    "freeShippingThreshold",
                    e.target.value ? parseFloat(e.target.value) : null,
                  )
                }
                size={isMobile ? "small" : "medium"}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AttachMoney />
                    </InputAdornment>
                  ),
                  inputProps: { min: 0, step: 0.01 },
                }}
                helperText="Order amount for free shipping"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size={isMobile ? "small" : "medium"}>
                <InputLabel>Shipping Method</InputLabel>
                <Select
                  value={settings.shippingMethod || ""}
                  onChange={(e) =>
                    handleChange("shippingMethod", e.target.value)
                  }
                  label="Shipping Method"
                >
                  <MenuItem value="flat">Flat Rate</MenuItem>
                  <MenuItem value="per_item">Per Item</MenuItem>
                  <MenuItem value="weight_based">Weight Based</MenuItem>
                </Select>
              </FormControl>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 0.5 }}
              >
                {settings.shippingMethod === "flat" &&
                  "Fixed shipping cost per order"}
                {settings.shippingMethod === "per_item" &&
                  "Shipping cost multiplied by number of items"}
                {settings.shippingMethod === "weight_based" &&
                  "Shipping cost based on total product weight"}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size={isMobile ? "small" : "medium"}>
                <InputLabel>Logistics Company</InputLabel>
                <Select
                  value={settings.logisticsCompany || ""}
                  onChange={(e) => {
                    const company = e.target.value;
                    const selected = logisticsCompanies.find(
                      (l) => l.company === company,
                    );
                    handleChange("logisticsCompany", company);
                    if (selected) {
                      handleChange(
                        "estimatedDelivery",
                        selected.estimatedDelivery || 3,
                      );
                    }
                  }}
                  label="Logistics Company"
                >
                  {logisticsCompanies.length > 0 ? (
                    logisticsCompanies.map((company) => (
                      <MenuItem
                        key={company.id || company.company}
                        value={company.company}
                      >
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <DeliveryDining fontSize="small" />
                          <span>
                            {company.company?.replace("_", " ").toUpperCase()}
                          </span>
                          {company.isActive && (
                            <Chip label="Active" size="small" color="success" />
                          )}
                        </Stack>
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem value="">No logistics companies available</MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>
            {selectedLogistics && (
              <Grid item xs={12}>
                <Box
                  sx={{
                    p: 2,
                    bgcolor: "#e8f5e9",
                    borderRadius: 2,
                    border: "1px solid #4caf50",
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    fontWeight="bold"
                    color="#2e7d32"
                    sx={{ mb: 1 }}
                  >
                    Logistics Details -{" "}
                    {selectedLogistics.company?.replace("_", " ").toUpperCase()}
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="caption" color="text.secondary">
                        Base Cost
                      </Typography>
                      <Typography variant="body2" fontWeight="600">
                        ETB {selectedLogistics.baseCost || 0}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="caption" color="text.secondary">
                        Distance Rate
                      </Typography>
                      <Typography variant="body2" fontWeight="600">
                        ETB {selectedLogistics.distanceRate || 0}/km
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="caption" color="text.secondary">
                        Weight Rate
                      </Typography>
                      <Typography variant="body2" fontWeight="600">
                        ETB {selectedLogistics.weightRate || 0}/kg
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="caption" color="text.secondary">
                        Est. Delivery
                      </Typography>
                      <Typography variant="body2" fontWeight="600">
                        {selectedLogistics.estimatedDelivery || 3} days
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Estimated Delivery (days)"
                type="number"
                value={settings.estimatedDelivery || 0}
                onChange={(e) =>
                  handleChange("estimatedDelivery", parseInt(e.target.value) || 0)
                }
                size={isMobile ? "small" : "medium"}
                InputProps={{
                  inputProps: { min: 1, max: 30 },
                }}
                helperText="Estimated delivery days"
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ p: 2, bgcolor: "#e3f2fd", borderRadius: 2 }}>
                <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
                  Summary
                </Typography>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={2}
                  divider={<Divider orientation="vertical" flexItem />}
                >
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Shipping Method
                    </Typography>
                    <Typography variant="body2" fontWeight="600">
                      {settings.shippingMethod?.replace("_", " ") || "Not set"}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Shipping Cost
                    </Typography>
                    <Typography variant="body2" fontWeight="600">
                      ETB {settings.shippingCost?.toFixed(2) || "0.00"}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Tax Rate
                    </Typography>
                    <Typography variant="body2" fontWeight="600">
                      {settings.taxRate || 0}%
                    </Typography>
                  </Box>
                  {settings.freeShippingThreshold && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Free Shipping Over
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight="600"
                        color="success.main"
                      >
                        ETB {settings.freeShippingThreshold.toFixed(2)}
                      </Typography>
                    </Box>
                  )}
                  {settings.logisticsCompany && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Logistics
                      </Typography>
                      <Typography variant="body2" fontWeight="600">
                        {settings.logisticsCompany.toUpperCase()}
                        {settings.estimatedDelivery > 0 &&
                          ` (${settings.estimatedDelivery} days)`}
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Preferences */}
      <Card sx={{ mb: 3, borderRadius: 3 }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
            <Language color="primary" />
            <Typography variant="h6" fontWeight="bold">
              Preferences
            </Typography>
          </Stack>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size={isMobile ? "small" : "medium"}>
                <InputLabel>Currency</InputLabel>
                <Select
                  value={settings.currency || ""}
                  onChange={(e) => handleChange("currency", e.target.value)}
                  label="Currency"
                >
                  <MenuItem value="ETB">ETB (Br)</MenuItem>
                  <MenuItem value="USD">USD ($)</MenuItem>
                  <MenuItem value="EUR">EUR (€)</MenuItem>
                  <MenuItem value="GBP">GBP (£)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size={isMobile ? "small" : "medium"}>
                <InputLabel>Language</InputLabel>
                <Select
                  value={settings.language || ""}
                  onChange={(e) => handleChange("language", e.target.value)}
                  label="Language"
                >
                  <MenuItem value="am">አማርኛ (Amharic)</MenuItem>
                  <MenuItem value="en">English</MenuItem>
                  <MenuItem value="om">Oromiffa (Oromo)</MenuItem>
                  <MenuItem value="ti">ትግርኛ (Tigrigna)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size={isMobile ? "small" : "medium"}>
                <InputLabel>Time Zone</InputLabel>
                <Select
                  value={settings.timezone || ""}
                  onChange={(e) => handleChange("timezone", e.target.value)}
                  label="Time Zone"
                >
                  <MenuItem value="Africa/Addis_Ababa">
                    Addis Ababa (GMT+3)
                  </MenuItem>
                  <MenuItem value="Africa/Nairobi">Nairobi (GMT+3)</MenuItem>
                  <MenuItem value="UTC">UTC</MenuItem>
                  <MenuItem value="America/New_York">New York (GMT-5)</MenuItem>
                  <MenuItem value="Europe/London">London (GMT+1)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Minimum Stock Alert"
                value={settings.minimumStockAlert || 0}
                onChange={(e) =>
                  handleChange(
                    "minimumStockAlert",
                    parseInt(e.target.value) || 0,
                  )
                }
                size={isMobile ? "small" : "medium"}
                InputProps={{ inputProps: { min: 0 } }}
                helperText="Get notified when stock falls below this number"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Settings;